import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Users,
  Briefcase,
  TrendingUp,
  Trash2,
  Eye,
  Search,
  Shield,
  LogOut,
  LayoutDashboard,
  X,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function AdminPanel() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [dataLoading, setDataLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPortfolios: 0,
    totalMatches: 0,
    publicPortfolios: 0,
    activelyLooking: 0,
  });

  const [users, setUsers] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      toast.error('Access denied. Admin only.');
      navigate('/');
      return;
    }
    loadData();
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    try {
      const [usersRes, portfoliosRes] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/users`, { withCredentials: true }),
        axios.get(`${API_BASE}/api/admin/portfolios`, { withCredentials: true }),
      ]);
      setUsers(usersRes.data);
      setPortfolios(portfoliosRes.data);
      const matches = await axios.get(`${API_BASE}/api/admin/matches`, { withCredentials: true });
      setStats({
        totalUsers: usersRes.data.length,
        totalPortfolios: portfoliosRes.data.length,
        totalMatches: matches.data.length,
        publicPortfolios: portfoliosRes.data.filter((p) => p.is_public).length,
        activelyLooking: portfoliosRes.data.filter((p) => p.actively_looking).length,
      });
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error(error.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setDataLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API_BASE}/api/admin/users/${userId}`, { withCredentials: true });
      toast.success('User deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleDeletePortfolio = async (portfolioId) => {
    if (!window.confirm('Are you sure you want to delete this portfolio?')) return;
    try {
      await axios.delete(`${API_BASE}/api/admin/portfolios/${portfolioId}`, { withCredentials: true });
      toast.success('Portfolio deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete portfolio');
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredPortfolios = portfolios.filter(
    (p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /* ── Loading ── */
  if (authLoading || dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#f5f1ed] to-[#fae5dd]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#e8744f] mx-auto mb-3" />
          <p className="text-sm text-gray-400 animate-pulse">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  const TABS = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'portfolios', label: 'Portfolios', icon: Briefcase },
  ];

  const STAT_CARDS = [
    { label: 'Total Users', value: stats.totalUsers, icon: Users, color: 'text-blue-500', bg: 'bg-blue-50' },
    { label: 'Portfolios', value: stats.totalPortfolios, icon: Briefcase, color: 'text-green-500', bg: 'bg-green-50' },
    { label: 'Matches', value: stats.totalMatches, icon: TrendingUp, color: 'text-purple-500', bg: 'bg-purple-50' },
    { label: 'Public', value: stats.publicPortfolios, icon: Eye, color: 'text-orange-500', bg: 'bg-orange-50' },
    { label: 'Looking', value: stats.activelyLooking, icon: Users, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f1ed] to-[#fae5dd]">

      {/* Top bar */}
      <header className="sticky top-0 z-50 border-b border-orange-100/60 bg-white/70 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">Home</span>
            </button>
            <div className="h-4 w-px bg-gray-200" />
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-red-500 to-red-600 shadow-sm">
                <Shield className="h-4 w-4 text-white" />
              </div>
              <h1 className="text-base font-bold text-gray-900 tracking-tight">Admin Panel</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex items-center gap-1 rounded-full border border-red-100 bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-600">
              <Shield size={10} /> Admin
            </span>
            <button
              onClick={async () => { await logout(); navigate('/login'); }}
              className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white/70 px-3 py-1.5 text-xs text-gray-600 hover:bg-white hover:text-red-500 transition"
            >
              <LogOut size={13} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 space-y-6">

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {STAT_CARDS.map(({ label, value, icon: Icon, color, bg }) => (
            <div
              key={label}
              className="rounded-2xl border border-orange-100 bg-white/70 p-4 shadow-sm flex flex-col items-center gap-2 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
            >
              <div className={`flex h-10 w-10 items-center justify-center rounded-full ${bg}`}>
                <Icon size={18} className={color} />
              </div>
              <span className="text-2xl font-bold text-gray-900">{value}</span>
              <span className="text-xs text-gray-400 text-center">{label}</span>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-orange-100 pb-0">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => { setActiveTab(id); setSearchQuery(''); }}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-xl border border-b-0 transition-all ${
                activeTab === id
                  ? 'bg-white border-orange-100 text-[#e8744f] shadow-sm -mb-px'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {/* ── Overview ── */}
        {activeTab === 'overview' && (
          <div className="rounded-2xl border border-orange-100 bg-white/70 shadow-sm p-6 space-y-5">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <LayoutDashboard size={18} className="text-[#e8744f]" />
              Platform Overview
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                {
                  title: 'User Growth',
                  value: `${stats.totalUsers} registered users`,
                  bg: 'bg-blue-50', border: 'border-blue-100', dot: 'bg-blue-400',
                },
                {
                  title: 'Portfolio Engagement',
                  value: `${stats.totalUsers > 0 ? ((stats.totalPortfolios / stats.totalUsers) * 100).toFixed(1) : 0}% of users have portfolios`,
                  bg: 'bg-green-50', border: 'border-green-100', dot: 'bg-green-400',
                },
                {
                  title: 'Match Success',
                  value: `${stats.totalMatches} successful matches`,
                  bg: 'bg-purple-50', border: 'border-purple-100', dot: 'bg-purple-400',
                },
                {
                  title: 'Public Visibility',
                  value: `${stats.totalPortfolios > 0 ? ((stats.publicPortfolios / stats.totalPortfolios) * 100).toFixed(1) : 0}% portfolios are public`,
                  bg: 'bg-orange-50', border: 'border-orange-100', dot: 'bg-orange-400',
                },
              ].map(({ title, value, bg, border, dot }) => (
                <div key={title} className={`rounded-xl border ${border} ${bg} p-4 space-y-1`}>
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${dot}`} />
                    <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
                  </div>
                  <p className="text-xs text-gray-500 pl-4">{value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Users ── */}
        {activeTab === 'users' && (
          <div className="rounded-2xl border border-orange-100 bg-white/70 shadow-sm p-6 space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Users size={18} className="text-[#e8744f]" />
                Manage Users
                <span className="text-xs font-normal text-gray-400">({filteredUsers.length})</span>
              </h2>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 pr-8 rounded-xl border-gray-200 text-sm w-56 focus:border-[#e8744f] focus:ring-2 focus:ring-orange-100 transition"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X size={13} />
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Name', 'Email', 'Role', 'Joined', 'Actions'].map((h) => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-orange-50/30 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-900">{u.name}</td>
                      <td className="px-4 py-3 text-gray-500">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                          u.role === 'admin'
                            ? 'bg-red-50 text-red-600 border border-red-100'
                            : 'bg-blue-50 text-blue-600 border border-blue-100'
                        }`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleDeleteUser(u.id)}
                          disabled={u.role === 'admin'}
                          className="flex items-center gap-1 rounded-lg border border-red-100 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-500 hover:bg-red-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredUsers.length === 0 && (
                <div className="py-12 text-center text-sm text-gray-400">No users found</div>
              )}
            </div>
          </div>
        )}

        {/* ── Portfolios ── */}
        {activeTab === 'portfolios' && (
          <div className="rounded-2xl border border-orange-100 bg-white/70 shadow-sm p-6 space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Briefcase size={18} className="text-[#e8744f]" />
                Manage Portfolios
                <span className="text-xs font-normal text-gray-400">({filteredPortfolios.length})</span>
              </h2>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search portfolios..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 pr-8 rounded-xl border-gray-200 text-sm w-56 focus:border-[#e8744f] focus:ring-2 focus:ring-orange-100 transition"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X size={13} />
                  </button>
                )}
              </div>
            </div>

            {filteredPortfolios.length === 0 ? (
              <div className="py-12 text-center text-sm text-gray-400">No portfolios found</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredPortfolios.map((portfolio) => (
                  <div
                    key={portfolio.id}
                    className="rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
                  >
                    {/* Card header */}
                    <div className="bg-gradient-to-br from-orange-50 to-white px-4 pt-4 pb-3 flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-[#e8744f] flex items-center justify-center text-white font-bold text-sm shrink-0">
                          {portfolio.name?.charAt(0)}
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-gray-900 truncate max-w-[140px]">{portfolio.name}</h3>
                          <p className="text-xs text-gray-400 truncate max-w-[140px]">{portfolio.title}</p>
                        </div>
                      </div>
                      {portfolio.actively_looking && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-600 border border-green-100 shrink-0">
                          Active
                        </span>
                      )}
                    </div>

                    {/* Stats row */}
                    <div className="px-4 py-2 flex items-center gap-3 text-xs text-gray-400 border-t border-gray-50">
                      <span className="flex items-center gap-1"><Eye size={11} />{portfolio.views} views</span>
                      <span>·</span>
                      <span>{portfolio.likes_received} likes</span>
                    </div>

                    {/* Actions */}
                    <div className="px-4 pb-4 pt-2 flex gap-2">
                      <button
                        onClick={() => navigate('/browse', { state: { startPortfolioId: portfolio.id } })}
                        className="flex-1 flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-gray-50 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 transition"
                      >
                        <Eye size={13} /> View
                      </button>
                      <button
                        onClick={() => handleDeletePortfolio(portfolio.id)}
                        className="flex items-center justify-center gap-1.5 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-medium text-red-500 hover:bg-red-100 transition"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}